import { html, nothing, svg, type TemplateResult } from "lit-html";

import { api, ApiError } from "../api.js";
import {
  decryptItem,
  deriveKekForLogin,
  encryptItem,
  persistUnlocked,
  unlockLocal,
  type UnlockedState,
} from "../client-crypto.js";
import { fromBase64, ulid } from "@shared/crypto/bytes.js";
import { copyToClipboard, rerender, showToast, state as appState } from "../main.js";

// Lucide-style stroke icons inline (no external dep).
// Keyed by CategoryDef.icon_name union.
const CAT_ICONS: Record<string, TemplateResult> = {
  mail: svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
  "shopping-cart": svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>`,
  "map-pin": svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>`,
  globe: svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`,
  "at-sign": svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"/></svg>`,
  "credit-card": svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>`,
  megaphone: svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>`,
  cloud: svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9"/></svg>`,
  ticket: svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></svg>`,
  wifi: svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13a10 10 0 0 1 14 0"/><path d="M8.5 16.5a5 5 0 0 1 7 0"/><path d="M2 8.82a15 15 0 0 1 20 0"/><line x1="12" x2="12.01" y1="20" y2="20"/></svg>`,
};

function catIcon(name: string): TemplateResult {
  return CAT_ICONS[name] ?? CAT_ICONS.cloud!;
}

interface RawItem {
  id: string;
  category_id: string;
  scope: "personal" | "company";
  version: number;
  name_ct_b64: string; name_iv_b64: string;
  ciphertext_b64: string; iv_b64: string;
  created_at: number; updated_at: number;
}

interface DecryptedListed {
  raw: RawItem;
  name: string;
  value: Record<string, unknown>;
}

const cache = new Map<string, DecryptedListed>();

let s = {
  loaded: false,
  items: [] as DecryptedListed[],
  err: "",
  showCreate: false,
  categories: [] as Array<{ id: string; slug: string; label_en: string; label_jp: string; icon_name: string; default_brand_color: string; order_index?: number }>,
  usefulLinks: [] as Array<{ id: string; label_en: string; label_jp: string | null; url: string; category: string | null; icon_name: string | null; order_index: number }>,
  newCategoryId: "",
  newName: "",
  newKvLogin: "",
  newKvPassword: "",
  newOpenLink: "",
  newNotes: "",
  busy: false,
  // Edit state — null when not editing, otherwise the item being edited
  editing: null as DecryptedListed | null,
  editName: "",
  editKvLogin: "",
  editKvPassword: "",
  editOpenLink: "",
  editNotes: "",
  // Inline unlock prompt state (shown when page-reload wiped in-memory KEK).
  unlockPassword: "",
  unlockBusy: false,
  unlockErr: "",
};

function startEdit(item: DecryptedListed): void {
  const v = item.value as { open_link?: string; kv?: Array<{ id: string; label: string; value: string; is_secret: boolean }>; notes?: string };
  s.editing = item;
  s.editName = item.name;
  s.editOpenLink = v.open_link ?? "";
  s.editNotes = v.notes ?? "";
  s.editKvLogin = v.kv?.find((r) => /login|email|user/i.test(r.label))?.value ?? "";
  s.editKvPassword = v.kv?.find((r) => r.is_secret)?.value ?? "";
  rerender();
}

function cancelEdit(): void {
  s.editing = null;
  rerender();
}

async function saveEdit(): Promise<void> {
  if (s.busy || !s.editing) return;
  const unlocked = (window as unknown as { __unlocked?: UnlockedState }).__unlocked;
  if (!unlocked) {
    showToast(appState.lang === "en" ? "Vault is locked" : "Vault がロックされています", "error");
    return;
  }
  s.busy = true;
  rerender();
  try {
    const it = s.editing.raw;
    const newValue = {
      v: 1,
      ...(s.editing.value as Record<string, unknown>),
      kv: [
        ...(s.editKvLogin ? [{ id: ulid(Date.now()), label: "Login", value: s.editKvLogin, is_secret: false }] : []),
        ...(s.editKvPassword ? [{ id: ulid(Date.now() + 1), label: "Password", value: s.editKvPassword, is_secret: true }] : []),
      ],
      ...(s.editOpenLink ? { open_link: s.editOpenLink } : { open_link: undefined }),
      ...(s.editNotes ? { notes: s.editNotes } : { notes: undefined }),
    };
    const recipients = await api.recipients();
    const recList = recipients.recipients.map((r) => ({
      user_id: r.user_id as string,
      ecdh_pub_jwk: r.ecdh_pub_jwk as string,
    }));
    const enc = await encryptItem({
      item_id: it.id,
      scope: it.scope,
      version: it.version + 1,
      name: s.editName,
      value: newValue,
      recipients: recList,
      unlocked,
    });
    await api.itemUpdate(it.id, {
      op: "update_value",
      prev_version: it.version,
      new_ciphertext_b64: enc.ciphertext_b64,
      new_iv_b64: enc.iv_b64,
    });
    // Name update is a separate AAD-bound operation. We always re-emit it
    // even if unchanged, to keep the wrapped name in lockstep with the new
    // value version (cheap and prevents future desync).
    await api.itemUpdate(it.id, {
      op: "update_name",
      new_name_ct_b64: enc.name_ct_b64,
      new_name_iv_b64: enc.name_iv_b64,
    });
    cache.delete(it.id);
    s.editing = null;
    s.loaded = false;
    await ensureLoaded();
    showToast(appState.lang === "en" ? "Item updated" : "アイテムを更新しました");
  } catch (err) {
    showToast((err as Error).message, "error");
  } finally {
    s.busy = false;
    rerender();
  }
}

async function deleteItem(item: DecryptedListed): Promise<void> {
  if (!confirm(appState.lang === "en"
    ? `Delete "${item.name}"? You can restore it from the admin console.`
    : `「${item.name}」を削除しますか？管理コンソールから復元できます。`)) return;
  try {
    await api.itemDelete(item.raw.id);
    cache.delete(item.raw.id);
    s.loaded = false;
    await ensureLoaded();
    showToast(appState.lang === "en" ? "Item deleted" : "アイテムを削除しました");
  } catch (err) {
    showToast((err as Error).message, "error");
  }
}

async function tryInlineUnlock(): Promise<void> {
  if (s.unlockBusy || !s.unlockPassword) return;
  s.unlockBusy = true;
  s.unlockErr = "";
  rerender();
  try {
    const me = await api.me();
    const wrapped = me.wrapped as unknown as {
      kdf_algo: "argon2id";
      kdf_params: { algo: "argon2id"; m: number; t: number; p: number; hashLength: number };
      kdf_salt_b64: string;
      ecdh_pub_jwk: string;
      wrapped_priv_b64: string;
      wrapped_ask_b64: string;
      wrapped_metadata_key_b64: string;
      signing_pub_jwk: string | null;
    };
    const kdfSalt = fromBase64(wrapped.kdf_salt_b64);
    const kek = await deriveKekForLogin({
      password: s.unlockPassword,
      kdfSalt,
      kdfParams: wrapped.kdf_params,
    });
    const unlocked = await unlockLocal({
      kek,
      wrapped_ask_b64: wrapped.wrapped_ask_b64,
      wrapped_priv_b64: wrapped.wrapped_priv_b64,
      wrapped_metadata_key_b64: wrapped.wrapped_metadata_key_b64,
      ecdh_pub_jwk: wrapped.ecdh_pub_jwk,
      signing_pub_jwk: wrapped.signing_pub_jwk,
    });
    (window as unknown as { __unlocked?: UnlockedState }).__unlocked = unlocked;
    persistUnlocked(unlocked);
    s.err = "";
    s.unlockPassword = "";
    s.loaded = false;
    showToast(appState.lang === "en" ? "Vault unlocked" : "Vault のロックを解除しました");
    await ensureLoaded();
  } catch (err) {
    s.unlockErr = appState.lang === "en"
      ? "Wrong password - unlock failed"
      : "パスワードが正しくありません - ロック解除に失敗しました";
    void err;
  } finally {
    s.unlockBusy = false;
    rerender();
  }
}

function renderInlineUnlock(): TemplateResult {
  const u = appState.user;
  const greet = u?.name?.split(" ")[0] ?? u?.email ?? "";
  return html`
    <div class="unlock-card">
      <div class="unlock-user">
        <div class="avatar">${(u?.name ?? u?.email ?? "?")[0]?.toUpperCase()}</div>
        <div>
          <div class="unlock-name">${u?.name ?? "-"}</div>
          <div class="unlock-email">${u?.email ?? ""}</div>
        </div>
      </div>
      <h2 class="unlock-h">
        <span data-lang="en">Welcome back${greet ? `, ${greet}` : ""}</span><span data-lang="jp">${greet ? `${greet}さん、` : ""}おかえりなさい</span>
      </h2>
      <p class="unlock-sub">
        <span data-lang="en">Enter the master password to unlock the vault.</span><span data-lang="jp">マスターパスワードを入力して Vault のロックを解除してください。</span>
      </p>
      <div class="form-row">
        <label>
          <span data-lang="en">Master password</span><span data-lang="jp">マスターパスワード</span>
        </label>
        <input type="password" autofocus autocomplete="current-password"
          .value=${s.unlockPassword}
          @input=${(e: InputEvent) => { s.unlockPassword = (e.target as HTMLInputElement).value; rerender(); }}
          @keydown=${(e: KeyboardEvent) => e.key === "Enter" && void tryInlineUnlock()} />
      </div>
      ${s.unlockErr ? html`<p class="unlock-err">${s.unlockErr}</p>` : nothing}
      <button class="primary unlock-cta"
        ?disabled=${s.unlockBusy || !s.unlockPassword}
        @click=${() => void tryInlineUnlock()}>
        ${s.unlockBusy
          ? html`<span data-lang="en">Unlocking…</span><span data-lang="jp">ロック解除中…</span>`
          : html`<span data-lang="en">Unlock vault</span><span data-lang="jp">Vault のロックを解除</span>`}
      </button>
    </div>
  `;
}

async function ensureLoaded(): Promise<void> {
  if (s.loaded) return;
  const unlocked = (window as unknown as { __unlocked?: UnlockedState }).__unlocked;
  if (!unlocked) {
    // No KEK in memory (page reload). Surface the inline unlock prompt.
    s.err = "__needs_unlock__";
    rerender();
    return;
  }
  try {
    const [resp, cats, links] = await Promise.all([
      api.itemsList(),
      api.categories(),
      api.usefulLinksList().catch(() => ({ links: [] })),
    ]);
    s.usefulLinks = links.links;
    const items: DecryptedListed[] = [];
    for (const raw of resp.items as unknown as RawItem[]) {
      if (cache.has(raw.id)) { items.push(cache.get(raw.id)!); continue; }
      // For listing we need the wrapped key per item; the list endpoint
      // does not include it. Call read for each (could be parallelized).
      try {
        const itemRead = await api.itemRead(raw.id);
        const dec = await decryptItem({
          item_id: raw.id,
          scope: raw.scope,
          version: raw.version,
          ciphertext_b64: itemRead.item.ciphertext_b64 as string,
          iv_b64: itemRead.item.iv_b64 as string,
          name_ct_b64: itemRead.item.name_ct_b64 as string,
          name_iv_b64: itemRead.item.name_iv_b64 as string,
          ephemeral_pub_jwk: itemRead.wrap.ephemeral_pub_jwk,
          wrapped_ck_b64: itemRead.wrap.wrapped_ck_b64,
          unlocked,
        });
        const item: DecryptedListed = { raw, name: dec.name, value: dec.value as Record<string, unknown> };
        cache.set(raw.id, item);
        items.push(item);
      } catch (err) {
        items.push({ raw, name: "(decrypt failed)", value: { _error: (err as Error).message } });
      }
    }
    s.items = items;
    s.categories = cats.categories as typeof s.categories;
    s.loaded = true;
  } catch (err) {
    s.err = err instanceof ApiError ? err.message : (err as Error).message;
  }
  rerender();
}

async function createItem(): Promise<void> {
  if (s.busy) return;
  const unlocked = (window as unknown as { __unlocked?: UnlockedState }).__unlocked;
  if (!unlocked) {
    showToast(appState.lang === "en" ? "Vault is locked" : "Vault がロックされています", "error");
    return;
  }
  if (!s.newName || !s.newCategoryId) {
    showToast(appState.lang === "en" ? "Name and category are required" : "名前とカテゴリは必須です", "error");
    return;
  }
  s.busy = true;
  try {
    const recipients = await api.recipients();
    const recList = recipients.recipients.map((r) => ({
      user_id: r.user_id as string,
      ecdh_pub_jwk: r.ecdh_pub_jwk as string,
    }));
    const itemId = ulid(Date.now());
    const value = {
      v: 1,
      logo: { kind: "initials", text: s.newName.slice(0, 2).toUpperCase() },
      brand_color: "#0078d4",
      open_link: s.newOpenLink || undefined,
      kv: [
        ...(s.newKvLogin ? [{ id: ulid(Date.now()+1), label: "Login", value: s.newKvLogin, is_secret: false }] : []),
        ...(s.newKvPassword ? [{ id: ulid(Date.now()+2), label: "Password", value: s.newKvPassword, is_secret: true }] : []),
      ],
      custom: [],
      ...(s.newNotes ? { notes: s.newNotes } : {}),
    };
    const enc = await encryptItem({
      item_id: itemId, scope: "company", version: 1,
      name: s.newName, value,
      recipients: recList,
      unlocked,
    });
    await api.itemsCreate({
      item_id: itemId,
      category_id: s.newCategoryId,
      scope: "company",
      name_ct_b64: enc.name_ct_b64,
      name_iv_b64: enc.name_iv_b64,
      ciphertext_b64: enc.ciphertext_b64,
      iv_b64: enc.iv_b64,
      wraps: enc.wraps,
    });
    s.newName = ""; s.newKvLogin = ""; s.newKvPassword = ""; s.newOpenLink = ""; s.newNotes = "";
    s.showCreate = false;
    s.loaded = false; // reload
    cache.clear();
    await ensureLoaded();
    showToast(appState.lang === "en" ? "Item created" : "アイテムを作成しました");
  } catch (err) {
    showToast((err as Error).message, "error");
  } finally {
    s.busy = false;
    rerender();
  }
}

// External-link arrow SVG (Lucide-style monochrome)
const OPEN_LINK_ICON = svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17L17 7"/><path d="M9 7h8v8"/></svg>`;

/**
 * Map a category slug to a CSS modifier class for the section box.
 * Drives the pastel background per category — one distinct palette per
 * category instead of just 4 buckets.
 */
function sectionClassFor(slug: string): string {
  if (slug.includes("google") || slug.includes("mail")) return "section-google";
  if (slug.includes("ota") || slug.includes("supplier")) return "section-ota";
  if (slug.includes("reservation")) return "section-reservations";
  if (slug.includes("website") || slug.includes("hosting")) return "section-website";
  if (slug.includes("domain") || slug.includes("registrar")) return "section-domain";
  if (slug.includes("payment")) return "section-payments";
  if (slug.includes("marketing") || slug.includes("social")) return "section-marketing";
  if (slug.includes("storage") || slug.includes("productivity")) return "section-storage";
  if (slug.includes("discount") || slug.includes("promo")) return "section-discount";
  if (slug.includes("office") || slug.includes("network")) return "section-net";
  return "section-tools";
}

/**
 * Map a category slug to the section tag label (top-right pillbox).
 */
function sectionTagFor(slug: string): { en: string; jp: string; warn: boolean } {
  if (slug.includes("google") || slug.includes("mail")) return { en: "Mail", jp: "メール", warn: false };
  if (slug.includes("ota") || slug.includes("supplier")) return { en: "Access", jp: "アクセス", warn: false };
  if (slug.includes("reservation")) return { en: "Bookings", jp: "予約", warn: false };
  if (slug.includes("website") || slug.includes("hosting")) return { en: "Web", jp: "ウェブ", warn: false };
  if (slug.includes("domain") || slug.includes("registrar")) return { en: "Domain", jp: "ドメイン", warn: false };
  if (slug.includes("payment")) return { en: "Pay", jp: "決済", warn: false };
  if (slug.includes("marketing") || slug.includes("social")) return { en: "Social", jp: "SNS", warn: false };
  if (slug.includes("storage") || slug.includes("productivity")) return { en: "Storage", jp: "ストレージ", warn: false };
  if (slug.includes("discount") || slug.includes("promo")) return { en: "Promo", jp: "プロモ", warn: false };
  if (slug.includes("office") || slug.includes("network")) return { en: "Wi-Fi", jp: "Wi-Fi", warn: true };
  return { en: "Tools", jp: "ツール", warn: false };
}

/**
 * Heuristic mapping from item name/category to a brand CSS class.
 * Drives the per-card --acct-color + --acct-soft variables.
 */
function brandClassFor(name: string, categorySlug: string): string {
  const lc = name.toLowerCase();
  if (/viator/.test(lc)) return "brand-viator";
  if (/klook/.test(lc)) return "brand-klook";
  if (/bokun/.test(lc)) return "brand-bokun";
  if (/byfood/.test(lc)) return "brand-byfood";
  if (/getyourguide|gyg/.test(lc)) return "brand-gyg";
  if (/airbnb/.test(lc)) return "brand-airbnb";
  if (/google|gmail|workspace/.test(lc)) return "brand-google";
  if (/microsoft|m365|365|outlook|office/.test(lc)) return "brand-microsoft";
  if (/dropbox/.test(lc)) return "brand-dropbox";
  if (/wix/.test(lc)) return "brand-wix";
  if (/line/.test(lc)) return "brand-line";
  if (/teams/.test(lc)) return "brand-teams";
  if (/wifi|wi-fi|network|router/.test(lc)) return "brand-network";
  // Fallback to the category brand color
  if (categorySlug.includes("google")) return "brand-google";
  if (categorySlug.includes("ota")) return "brand-klook";
  if (categorySlug.includes("network")) return "brand-network";
  return "brand-microsoft";
}

/**
 * Map an item name/category to a sprite symbol id (loaded from
 * /sprite.svg into the DOM at bootstrap). Returns null if no match;
 * caller falls back to initials.
 */
function spriteIdFor(name: string, categorySlug: string): string | null {
  const lc = name.toLowerCase();
  if (/viator/.test(lc)) return "ico-viator";
  if (/klook/.test(lc)) return "ico-klook";
  if (/bokun/.test(lc)) return "ico-bokun";
  if (/byfood/.test(lc)) return "ico-byfood";
  if (/getyourguide|gyg/.test(lc)) return "ico-gyg";
  if (/airbnb/.test(lc)) return "ico-airbnb";
  if (/booking\.com/.test(lc)) return "ico-booking";
  if (/gmail/.test(lc)) return "ico-gmail";
  if (/google\s*workspace|website\s*manager/.test(lc)) return "ico-google-workspace";
  if (/google/.test(lc)) return "ico-google-g";
  if (/microsoft|m365|outlook|office\s*365/.test(lc)) return "ico-microsoft";
  if (/dropbox/.test(lc)) return "ico-dropbox";
  if (/wix/.test(lc)) return "ico-wix";
  if (/line/.test(lc)) return "ico-line";
  if (/teams/.test(lc)) return "ico-microsoft";
  if (/chrome/.test(lc)) return "ico-chrome";
  if (/canva/.test(lc)) return "ico-canva";
  if (/mailchimp/.test(lc)) return "ico-mailchimp";
  if (/meta|facebook|instagram/.test(lc)) return "ico-meta";
  if (/stripe/.test(lc)) return "ico-stripe";
  if (/wi-?fi|router|ruckus|anta-?net|vmg/.test(lc)) return "ico-wifi";
  if (categorySlug.includes("office") || categorySlug.includes("network")) return "ico-wifi";
  if (categorySlug.includes("website") || categorySlug.includes("hosting") || categorySlug.includes("domain")) return "ico-building";
  return null;
}

/**
 * Render a small inline brand glyph (sprite if matched, initials
 * otherwise) inside the .acct-card-logo box.
 */
function brandGlyphFor(name: string, categorySlug: string): TemplateResult {
  const spriteId = spriteIdFor(name, categorySlug);
  if (spriteId) {
    return svg`<svg viewBox="0 0 32 32" style="width:100%;height:100%;" aria-hidden="true"><use href="#${spriteId}" xlink:href="#${spriteId}"/></svg>`;
  }
  const initials = name.split(/[\s\-_]+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || "?";
  return html`<span>${initials.slice(0, 3)}</span>`;
}

function cardTemplate(item: DecryptedListed): TemplateResult {
  const v = item.value as {
    logo?: { kind: string; text?: string; url?: string; name?: string };
    brand_color?: string;
    subtitle?: string;
    open_link?: string;
    kv?: Array<{ id: string; label: string; value: string; is_secret: boolean }>;
    notes?: string;
  };
  const cat = s.categories.find((c) => c.id === item.raw.category_id);
  const catSlug = cat?.slug ?? "";
  const brandCls = brandClassFor(item.name, catSlug);
  const copyTitle = appState.lang === "en" ? "Click to copy" : "クリックでコピー";
  // Favicon URLs from the seed are unreliable (CORS, network, hot-link
  // blocks) so we always render the sprite/initials glyph instead. The
  // sprite has all the major brands; everything else falls back to
  // styled initials in the card-color box.
  return html`
    <div class="acct-card ${brandCls}">
      <div class="acct-card-hd">
        <div class="acct-card-logo" aria-hidden="true">
          ${brandGlyphFor(item.name, catSlug)}
        </div>
        <div class="acct-card-name">
          ${item.name}
          ${v.subtitle ? html`<span class="sub">${v.subtitle}</span>` : nothing}
        </div>
        ${v.open_link
          ? html`<a class="acct-card-open" href=${v.open_link} target="_blank" rel="noopener">
              <span data-lang="en">Open</span><span data-lang="jp">開く</span>
              ${OPEN_LINK_ICON}
            </a>`
          : nothing}
      </div>
      <div class="acct-kv">
        ${(v.kv ?? []).map((row) => html`
          <div class="k">${row.label}</div>
          ${row.is_secret
            ? html`<div class="v"><span class="secret" title=${copyTitle}
                @click=${() => copyToClipboard(row.value)}>${row.value}</span></div>`
            : html`<div class="v copyable" title=${copyTitle}
                @click=${() => copyToClipboard(row.value)}>${row.value}</div>`}
        `)}
        ${v.open_link
          ? html`<div class="k">URL</div>
              <div class="v"><a class="url-link" href=${v.open_link} target="_blank" rel="noopener">${v.open_link}</a></div>`
          : nothing}
      </div>
      ${v.notes ? html`<p class="acct-card-notes">${v.notes}</p>` : nothing}
      <div class="acct-card-actions">
        <button class="ghost-btn" @click=${() => startEdit(item)}>
          <span data-lang="en">Edit</span><span data-lang="jp">編集</span>
        </button>
        ${appState.user?.role !== "EMPLOYEE" || item.raw.scope === "personal"
          ? html`<button class="ghost-btn danger" @click=${() => void deleteItem(item)}>
              <span data-lang="en">Delete</span><span data-lang="jp">削除</span>
            </button>`
          : nothing}
      </div>
    </div>
  `;
}

function categorySection(cat: typeof s.categories[number], items: DecryptedListed[]): TemplateResult {
  const label = appState.lang === "en" ? cat.label_en : cat.label_jp;
  const sectionCls = sectionClassFor(cat.slug);
  const tag = sectionTagFor(cat.slug);
  return html`
    <section class="category-section ${sectionCls}">
      <div class="cat-header">
        <span class="cat-icon">${catIcon(cat.icon_name)}</span>
        <span class="cat-label">${label}</span>
        <span class="cat-tag ${tag.warn ? "tag-warn" : ""}">
          <span data-lang="en">${tag.en}</span><span data-lang="jp">${tag.jp}</span>
        </span>
      </div>
      <div class="cards">
        ${items.map(cardTemplate)}
      </div>
    </section>
  `;
}

export function renderVault(): TemplateResult {
  void ensureLoaded();
  if (s.err === "__needs_unlock__") return renderInlineUnlock();
  if (s.err) return html`<div class="vault-page"><p style="color: var(--status-error);">${s.err}</p></div>`;
  if (!s.loaded) return html`<div class="vault-page"><p>
    <span data-lang="en">Decrypting…</span><span data-lang="jp">復号中…</span>
  </p></div>`;

  // Group items by category in order_index ascending; uncategorized at end.
  const byCat = new Map<string, DecryptedListed[]>();
  for (const it of s.items) {
    const arr = byCat.get(it.raw.category_id) ?? [];
    arr.push(it);
    byCat.set(it.raw.category_id, arr);
  }
  const orderedCats = [...s.categories].sort((a, b) => (a.order_index ?? 99) - (b.order_index ?? 99));

  // True row-first masonry: distribute categories across N columns by
  // picking the shortest column for each one. Without this CSS grid
  // would leave huge vertical gaps when one section is much taller
  // than its row neighbors.
  function pickColumnCount(): number {
    const w = window.innerWidth;
    if (w >= 1280) return 3;
    if (w >= 760) return 2;
    return 1;
  }
  const nCols = pickColumnCount();
  const cols: Array<{ items: Array<{ cat: typeof s.categories[number]; items: DecryptedListed[] }>; weight: number }>
    = Array.from({ length: nCols }, () => ({ items: [], weight: 0 }));
  function pushToShortest(cat: typeof s.categories[number], items: DecryptedListed[]): void {
    let target = 0;
    for (let i = 1; i < cols.length; i++) {
      if (cols[i]!.weight < cols[target]!.weight) target = i;
    }
    cols[target]!.items.push({ cat, items });
    // weight = card count + small header constant
    cols[target]!.weight += items.length + 1;
  }
  for (const cat of orderedCats) {
    const items = byCat.get(cat.id);
    if (!items?.length) continue;
    pushToShortest(cat, items);
    byCat.delete(cat.id);
  }
  for (const [catId, items] of byCat.entries()) {
    pushToShortest(
      { id: catId, slug: catId, label_en: catId, label_jp: catId, icon_name: "cloud", default_brand_color: "#7a7a85" },
      items,
    );
  }

  return html`
    <main class="vault-page">
      <div class="vault-toolbar">
        <button class="primary" @click=${() => { s.showCreate = !s.showCreate; rerender(); }}>
          ${s.showCreate
            ? html`<span data-lang="en">Cancel</span><span data-lang="jp">キャンセル</span>`
            : html`<span data-lang="en">+ New item</span><span data-lang="jp">+ 新規アイテム</span>`}
        </button>
        <span class="count">
          <span data-lang="en">${s.items.length} item${s.items.length === 1 ? "" : "s"}</span><span data-lang="jp">${s.items.length} 件</span>
        </span>
      </div>
      ${s.editing ? html`
        <div class="edit-overlay" @click=${(e: Event) => { if ((e.target as HTMLElement).classList.contains("edit-overlay")) cancelEdit(); }}>
          <div class="edit-card">
            <h2 style="margin: 0 0 16px;">
              <span data-lang="en">Edit item</span><span data-lang="jp">アイテム編集</span>
            </h2>
            <div class="form-row">
              <label><span data-lang="en">Name</span><span data-lang="jp">名前</span></label>
              <input type="text" .value=${s.editName} @input=${(e: InputEvent) => (s.editName = (e.target as HTMLInputElement).value)} />
            </div>
            <div class="form-row">
              <label><span data-lang="en">Login</span><span data-lang="jp">ログイン</span></label>
              <input type="text" .value=${s.editKvLogin} @input=${(e: InputEvent) => (s.editKvLogin = (e.target as HTMLInputElement).value)} />
            </div>
            <div class="form-row">
              <label><span data-lang="en">Password</span><span data-lang="jp">パスワード</span></label>
              <input type="text" .value=${s.editKvPassword} @input=${(e: InputEvent) => (s.editKvPassword = (e.target as HTMLInputElement).value)} />
            </div>
            <div class="form-row">
              <label><span data-lang="en">Open link (https://)</span><span data-lang="jp">開くリンク (https://)</span></label>
              <input type="url" .value=${s.editOpenLink} @input=${(e: InputEvent) => (s.editOpenLink = (e.target as HTMLInputElement).value)} />
            </div>
            <div class="form-row">
              <label><span data-lang="en">Notes</span><span data-lang="jp">メモ</span></label>
              <textarea rows="3" .value=${s.editNotes} @input=${(e: InputEvent) => (s.editNotes = (e.target as HTMLTextAreaElement).value)}></textarea>
            </div>
            <div style="display: flex; gap: 8px; justify-content: flex-end;">
              <button class="ghost-btn" @click=${cancelEdit}>
                <span data-lang="en">Cancel</span><span data-lang="jp">キャンセル</span>
              </button>
              <button class="primary" ?disabled=${s.busy} @click=${() => void saveEdit()}>
                ${s.busy
                  ? html`<span data-lang="en">Saving…</span><span data-lang="jp">保存中…</span>`
                  : html`<span data-lang="en">Save</span><span data-lang="jp">保存</span>`}
              </button>
            </div>
          </div>
        </div>
      ` : nothing}
      ${s.showCreate ? html`
        <div style="max-width: 720px; margin: 16px auto; background: var(--surface-1); padding: 24px; border-radius: 12px; border: 1px solid var(--border-1);">
          <div class="form-row">
            <label><span data-lang="en">Category</span><span data-lang="jp">カテゴリ</span></label>
            <select @change=${(e: Event) => (s.newCategoryId = (e.target as HTMLSelectElement).value)}>
              <option value="">${appState.lang === "en" ? "-- pick --" : "-- 選択 --"}</option>
              ${s.categories.map((c) => html`<option value=${c.id}>${c.label_en} (${c.label_jp})</option>`)}
            </select>
          </div>
          <div class="form-row">
            <label><span data-lang="en">Item name</span><span data-lang="jp">アイテム名</span></label>
            <input type="text" .value=${s.newName} @input=${(e: InputEvent) => (s.newName = (e.target as HTMLInputElement).value)} />
          </div>
          <div class="form-row">
            <label><span data-lang="en">Login (optional)</span><span data-lang="jp">ログイン (任意)</span></label>
            <input type="text" .value=${s.newKvLogin} @input=${(e: InputEvent) => (s.newKvLogin = (e.target as HTMLInputElement).value)} />
          </div>
          <div class="form-row">
            <label><span data-lang="en">Password (optional)</span><span data-lang="jp">パスワード (任意)</span></label>
            <input type="text" .value=${s.newKvPassword} @input=${(e: InputEvent) => (s.newKvPassword = (e.target as HTMLInputElement).value)} />
          </div>
          <div class="form-row">
            <label><span data-lang="en">Open link (optional, https://)</span><span data-lang="jp">開くリンク (任意、https://)</span></label>
            <input type="url" .value=${s.newOpenLink} @input=${(e: InputEvent) => (s.newOpenLink = (e.target as HTMLInputElement).value)} />
          </div>
          <div class="form-row">
            <label><span data-lang="en">Notes (optional)</span><span data-lang="jp">メモ (任意)</span></label>
            <textarea rows="3" .value=${s.newNotes} @input=${(e: InputEvent) => (s.newNotes = (e.target as HTMLTextAreaElement).value)}></textarea>
          </div>
          <button class="primary" ?disabled=${s.busy} @click=${() => void createItem()}>
            <span data-lang="en">Create</span><span data-lang="jp">作成</span>
          </button>
        </div>
      ` : nothing}
      <div class="vault-cols">
        ${cols.map((col) => html`
          <div class="vault-col">
            ${col.items.map(({ cat, items }) => categorySection(cat, items))}
          </div>
        `)}
      </div>
      ${renderVaultFooter()}
    </main>
  `;
}

function renderVaultFooter(): TemplateResult {
  // Footer always rendered. If no links: employees see nothing, SU/Admin
  // see an empty-state pillbox pointing to the admin tab so they know
  // the feature exists.
  if (s.usefulLinks.length === 0) {
    const role = appState.user?.role;
    if (role !== "SUPER_USER" && role !== "ADMIN") return html``;
    return html`
      <footer class="vault-footer">
        <div class="vault-footer-hd">
          <span class="vault-footer-title">
            <span data-lang="en">Useful links</span><span data-lang="jp">便利なリンク</span>
          </span>
        </div>
        <p style="font-size: 13px; color: var(--text-2); margin: 8px 0 0;">
          <span data-lang="en">No links yet. Open the <b>Admin → Useful links</b> tab to add Chrome, Gmail, LINE, schedules, etc. — visible to every authenticated user.</span><span data-lang="jp">リンクはまだありません。<b>管理 → 便利リンク</b> タブから Chrome / Gmail / LINE / スケジュール等を追加できます。認証済みの全ユーザーに表示されます。</span>
        </p>
      </footer>
    `;
  }
  return html`
    <footer class="vault-footer">
      <div class="vault-footer-hd">
        <span class="vault-footer-title">
          <span data-lang="en">Useful links</span><span data-lang="jp">便利なリンク</span>
        </span>
      </div>
      <div class="vault-footer-grid">
        ${s.usefulLinks.map((l) => {
          const label = appState.lang === "en" ? l.label_en : (l.label_jp ?? l.label_en);
          return html`<a class="vault-footer-link" href=${l.url} target="_blank" rel="noopener" title=${l.url}>
            ${label}
          </a>`;
        })}
      </div>
    </footer>
  `;
}
